// Syncs the single-image product gallery with its thumbnail strip and arrows,
// and jumps to a variant's assigned image when the shopper picks that variant (e.g. a color).
class SProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-product-gallery]');
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    if (!this.track || this.thumbs.length === 0) return;
    this.ac = new AbortController();
    const opts = { signal: this.ac.signal };
    this.index = 0;
    this.variantMedia = this.parseVariantMedia();
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i), opts));
    this.prev?.addEventListener('click', () => this.go(this.index - 1), opts);
    this.next?.addEventListener('click', () => this.go(this.index + 1), opts);
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.sync());
    }, { passive: true, signal: this.ac.signal });

    // Native options and independent bundle rows both preview their selected variant image.
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement)) return;
      const nativePicker = input.closest('s-variant-picker');
      const bundlePicker = input.matches('[data-cb-variant]') && input.closest('s-christmas-bundle');
      const mensBundleRow = input.matches('[data-bundle-row]') && input.closest('s-product-bundle');
      if (!nativePicker && !bundlePicker && !mensBundleRow) return;
      const variantId = bundlePicker ? input.value : input.dataset.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
      const mediaIndex = variantId ? this.variantMedia.get(variantId) : undefined;
      if (mediaIndex !== undefined) this.go(mediaIndex);
    }, opts);

    // Every product entry starts on its cover, including links with a selected variant.
    // Variant images are previewed only after a shopper changes a selection.
    this.go(0, 'instant');
  }

  disconnectedCallback() {
    this.ac?.abort();
    cancelAnimationFrame(this.frame);
  }

  parseVariantMedia() {
    const map = new Map();
    (this.dataset.variantMedia || '').trim().split(/\s+/).forEach((pair) => {
      const [variantId, index] = pair.split(':');
      const position = Number.parseInt(index, 10);
      if (variantId && Number.isInteger(position) && position >= 0) map.set(variantId, position);
    });
    return map;
  }

  slides() {
    return [...this.track.children];
  }

  go(i, behavior) {
    const slides = this.slides();
    const target = Math.max(0, Math.min(i, slides.length - 1));
    const slide = slides[target];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, ...(behavior ? { behavior } : {}) });
    this.index = target;
    this.update();
  }

  sync() {
    const width = this.track.clientWidth || 1;
    const i = Math.round(Math.abs(this.track.scrollLeft) / width);
    if (i !== this.index) {
      this.index = i;
      this.update();
    }
  }

  update() {
    this.thumbs.forEach((thumb, i) => {
      if (i === this.index) {
        thumb.setAttribute('aria-current', 'true');
        const strip = thumb.parentElement;
        const left = thumb.offsetLeft - strip.offsetLeft;
        if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
          strip.scrollTo({ left: left - (strip.clientWidth - thumb.offsetWidth) / 2 });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    if (this.prev) this.prev.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index >= this.thumbs.length - 1;
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
